import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useToast } from '../../shared/contexts/ToastContext';
import EnChangeHistoryModal from './EnChangeHistoryModal';

export interface TranslationResult {
  sid: string;
  runs: string;
  enter_flag: boolean;
}

interface TranslationTableProps {
  translationResults: TranslationResult[];
  currentDocumentId: string | null;
  isUploading: boolean;
  editingIndex: number | null;
  editingText: string;
  selectedRows: Set<string>;
  isSelectionMode: boolean;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
  selectedCompany: { name: string; company_id?: number; id: string } | null;
  searchTerm: string;
  onBackToOptions: () => void;
  onStartTranslation: () => void;
  onStartSelectionMode: () => void;
  onExitSelectionMode: () => void;
  onShowBulkDeleteModal: () => void;
  onShowDeleteChangesModal: () => void;
  onItemsPerPageChange: (value: number) => void;
  onCurrentPageChange: (page: number) => void;
  onRowSelect: (sid: string) => void;
  onEditStart: (index: number, text: string) => void;
  onEditSave: (index: number) => void;
  onEditCancel: () => void;
  onEditingTextChange: (text: string) => void;
  onShowDeleteRowModal: (sid: string) => void;
  onSearchTermChange: (term: string) => void;
  onGoToRow: (rowNumber: number, targetSid: string) => void;
  parseRunsText: (runs: string) => React.ReactNode;
  calculateRows: (text: string) => number;
  onDragDropUpdate?: (sourceSid: string, targetSid: string, combinedText: string, emptyText: string) => Promise<void>;
  showToast?: (message: string, type: 'success' | 'error') => void;
  onRefreshData?: () => Promise<void>;
}

const TranslationTable: React.FC<TranslationTableProps> = ({
  translationResults,
  currentDocumentId,
  isUploading,
  editingIndex,
  editingText,
  selectedRows,
  isSelectionMode,
  currentPage,
  itemsPerPage,
  totalItems,
  totalPages,
  selectedCompany,
  searchTerm,
  onBackToOptions,
  onStartTranslation,
  onStartSelectionMode,
  onExitSelectionMode,
  onShowBulkDeleteModal,
  onShowDeleteChangesModal,
  onItemsPerPageChange,
  onCurrentPageChange,
  onRowSelect,
  onEditStart,
  onEditSave,
  onEditCancel,
  onEditingTextChange,
  onShowDeleteRowModal,
  onSearchTermChange,
  onGoToRow,
  parseRunsText,
  calculateRows,
  onDragDropUpdate,
  showToast,
  onRefreshData
}) => {
  // Go to row state
  const [goToRowValue, setGoToRowValue] = React.useState<string>('');
  const [highlightedRowSid, setHighlightedRowSid] = React.useState<string | null>(null);
  
  // Search results and navigation
  const [searchResults, setSearchResults] = React.useState<{sid: string, index: number}[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = React.useState<number>(0);

  // Drag and drop states for Turkish text
  const [draggedRowSid, setDraggedRowSid] = React.useState<string | null>(null);
  const [dragOverRowSid, setDragOverRowSid] = React.useState<string | null>(null);
  const [isDragging, setIsDragging] = React.useState<boolean>(false);

  // Pie menu states
  const [showPageSizePieMenu, setShowPageSizePieMenu] = React.useState<boolean>(false);
  
  // Timeout ref for debounced go to row
  const goToRowTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Scroll position preservation
  const scrollPositionRef = React.useRef<number>(0);
  const preserveScrollRef = React.useRef<boolean>(false);
  
  // Search state preservation (page and scroll position before search)
  const searchStateBeforeSearchRef = React.useRef<{page: number, scrollPosition: number} | null>(null);
  
  // Function to get the scroll container
  const getScrollContainer = () => {
    return (
      document.querySelector('[data-testid="translation-table-scroll-container"]') ||
      document.querySelector('[data-testid="translation-table-main-container"]') ||
      document.querySelector('[data-testid="translation-table-container"]') ||
      document.querySelector('.overflow-auto.flex-1') ||
      document.querySelector('.overflow-y-scroll') ||
      document.querySelector('.overflow-y-auto') ||
      document.querySelector('.max-h-screen') ||
      document.querySelector('.h-screen') ||
      document.querySelector('table')?.closest('.overflow-y-scroll, .overflow-y-auto, [style*="height"]') ||
      document.querySelector('[style*="calc(100vh"]')
    );
  };
  
  // Function to save scroll position
  const saveScrollPosition = () => {
    const container = getScrollContainer();
    if (container) {
      scrollPositionRef.current = container.scrollTop;
      preserveScrollRef.current = true;
      console.log('💾 Scroll pozisyonu kaydedildi:', scrollPositionRef.current);
    }
  };
  
  // Function to restore scroll position
  const restoreScrollPosition = () => {
    if (preserveScrollRef.current) {
      setTimeout(() => {
        const container = getScrollContainer();
        if (container && scrollPositionRef.current > 0) {
          container.scrollTo({ top: scrollPositionRef.current, behavior: 'smooth' });
          console.log('🔄 Scroll pozisyonu geri yüklendi:', scrollPositionRef.current);
        }
        preserveScrollRef.current = false;
      }, 100);
    }
  };

  // Handle page change with scroll to top
  const handlePageChange = (page: number) => {
    onCurrentPageChange(page);
    // Scroll to top of the table container
    setTimeout(() => {
      const tableContainer = getScrollContainer();
      
      if (tableContainer) {
        tableContainer.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Fallback: scroll to top of window
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100); // Small delay to ensure page change is processed
  };

  // Find and Replace states
  const [showReplaceInput, setShowReplaceInput] = useState<boolean>(false);
  const [replaceText, setReplaceText] = useState<string>('');
  const [isFindReplaceProcessing, setIsFindReplaceProcessing] = useState<boolean>(false);
  const [replacePreview, setReplacePreview] = useState<{sid: string, oldText: string, newText: string, rowNumber: number}[]>([]);

  // Change History states
  const [changeHistory, setChangeHistory] = useState<any[]>([]);
  const [showChangeHistoryModal, setShowChangeHistoryModal] = useState<boolean>(false);
  const [selectedRowChangeHistory, setSelectedRowChangeHistory] = useState<{sid: string, rowNumber: number} | null>(null);

  // Filter states
  const [showChangedRowsOnly, setShowChangedRowsOnly] = useState<boolean>(false);

  // Search options
  const [isCaseSensitive, setIsCaseSensitive] = useState<boolean>(false);
  const [isWholeWord, setIsWholeWord] = useState<boolean>(false);

  // Pagination calculations
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Normalize Turkish text for search
  const normalizeTurkish = (text: string) => {
    return text.toLocaleLowerCase('tr-TR');
  };

  // Apply filters to translationResults before pagination
  const filteredResults = React.useMemo(() => {
    let filtered = [...translationResults];
    
    // Filter by changed rows only
    if (showChangedRowsOnly) {
      filtered = filtered.filter(item => {
        const hasChanges = changeHistory?.some((historyItem: any) => 
          historyItem.sid === item.sid && historyItem.changes && historyItem.changes.length > 0
        );
        return hasChanges;
      });
    }

    // Filter by search term — arama aktifken sadece eşleşen satırları göster
    if (searchTerm.trim()) {
      filtered = filtered.filter(item => {
        const text = parseRunsText(item.runs) as string || '';
        if (isWholeWord) {
          if (isCaseSensitive) {
            const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escaped})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
            return regex.test(text);
          } else {
            const normalizedText = normalizeTurkish(text);
            const normalizedSearch = normalizeTurkish(searchTerm);
            const escaped = normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escaped})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
            return regex.test(normalizedText);
          }
        } else {
          if (isCaseSensitive) {
            return text.includes(searchTerm);
          } else {
            return normalizeTurkish(text).includes(normalizeTurkish(searchTerm));
          }
        }
      });
    }
    
    return filtered;
  }, [translationResults, showChangedRowsOnly, changeHistory, searchTerm, isCaseSensitive, isWholeWord]);
  
  const currentItems = filteredResults.slice(startIndex, endIndex);
  
  // Use filtered results for pagination
  const actualTotalItems = filteredResults.length;
  const actualTotalPages = Math.ceil(actualTotalItems / itemsPerPage);
  
  // Restore scroll position when translation results change (after refresh)
  React.useEffect(() => {
    restoreScrollPosition();
  }, [translationResults]);

  // Auth and Toast contexts
  const { user } = useAuth();
  const { success, error: showError } = useToast();

  // Handle go to row
  const handleGoToRow = () => {
    const rowNumber = parseInt(goToRowValue);
    if (rowNumber && rowNumber >= 1 && rowNumber <= translationResults.length) {
      // Find the target row's sid
      const targetRow = translationResults[rowNumber - 1];
      if (targetRow) {
        // Set highlighted row
        setHighlightedRowSid(targetRow.sid);
        
        // Clear highlight after animation
        setTimeout(() => {
          setHighlightedRowSid(null);
        }, 2000);
        
        // Navigate to the correct page and scroll to row
        onGoToRow(rowNumber, targetRow.sid);
        setGoToRowValue('');
      }
    }
  };

  // Handle go to row change with debounce
  const handleGoToRowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setGoToRowValue(value);
      
      // Clear previous timeout
      if (goToRowTimeoutRef.current) {
        clearTimeout(goToRowTimeoutRef.current);
      }
      
      // If empty, don't set timeout
      if (value === '') {
        return;
      }
      
      // Debounce: Navigate after 500ms
      goToRowTimeoutRef.current = setTimeout(() => {
        const rowNumber = parseInt(value);
        const totalRows = translationResults?.length || 0;
        
        if (rowNumber && rowNumber >= 1 && rowNumber <= totalRows) {
          // Calculate which page the row is on
          const targetPage = Math.ceil(rowNumber / itemsPerPage);
          handlePageChange(targetPage);
          
          // Find the target row's sid
          const targetRow = translationResults?.[rowNumber - 1];
          if (targetRow) {
            // Scroll to the row after a short delay to ensure page change
            setTimeout(() => {
              const rowElement = document.getElementById(`translation-row-${targetRow.sid}`);
              if (rowElement) {
                rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 200);
          }
        }
      }, 500); // 500ms debounce
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (goToRowTimeoutRef.current) {
        clearTimeout(goToRowTimeoutRef.current);
      }
    };
  }, []);

  // Search results calculation
  const calculateSearchResults = (data: typeof translationResults, searchTerm: string) => {
    if (!data || !searchTerm.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    const results: {sid: string, index: number}[] = [];
    
    data.forEach((item, index) => {
      const text = parseRunsText(item.runs) as string || '';
      
      let isMatch = false;
      
      if (isWholeWord) {
        // Whole word matching - use custom word boundaries for better Turkish support
        if (isCaseSensitive) {
          // Case sensitive: use original text and search term
          const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Match word only if surrounded by whitespace, punctuation, or start/end of string
          const regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchTerm})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
          isMatch = regex.test(text);
        } else {
          // Case insensitive: normalize both text and search term (for Turkish chars)
          const normalizedText = normalizeTurkish(text);
          const normalizedSearchTerm = normalizeTurkish(searchTerm);
          const escapedSearchTerm = normalizedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchTerm})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
          isMatch = regex.test(normalizedText);
        }
      } else {
        // Simple substring matching
        if (isCaseSensitive) {
          isMatch = text.includes(searchTerm);
        } else {
          const normalizedText = normalizeTurkish(text);
          const normalizedSearchTerm = normalizeTurkish(searchTerm);
          isMatch = normalizedText.includes(normalizedSearchTerm);
        }
      }
      
      if (isMatch) {
        results.push({ sid: item.sid, index: index + 1 });
      }
    });
    
    setSearchResults(results);
    setCurrentSearchIndex(0);

    // Arama aktifken sayfa 1'e git (filtrelenmiş sonuçlar baştan gösterilir)
    onCurrentPageChange(1);

    // İlk sonucu vurgula
    if (results.length > 0) {
      setHighlightedRowSid(results[0].sid);
      setTimeout(() => setHighlightedRowSid(null), 3000);
    }
  };

  // Jump to specific search result
  const jumpToSearchResult = (resultIndex: number, results = searchResults) => {
    if (results.length === 0 || resultIndex < 0 || resultIndex >= results.length) return;
    
    const result = results[resultIndex];
    const targetRow = translationResults.find(item => item.sid === result.sid);
    
    if (targetRow) {
      // Set highlighted row
      setHighlightedRowSid(targetRow.sid);
      
      // Clear highlight after animation
      setTimeout(() => {
        setHighlightedRowSid(null);
      }, 3000);
      
      // Arama aktifken filtrelenmiş listedeki pozisyonu kullan
      const filteredIndex = filteredResults.findIndex(item => item.sid === result.sid);
      const position = filteredIndex !== -1 ? filteredIndex + 1 : result.index;
      onGoToRow(position, targetRow.sid);
    }
  };

  // Navigate to next search result
  const goToNextSearchResult = () => {
    if (searchResults.length === 0) return;
    
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    jumpToSearchResult(nextIndex);
  };

  // Navigate to previous search result
  const goToPreviousSearchResult = () => {
    if (searchResults.length === 0) return;
    
    const prevIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    jumpToSearchResult(prevIndex);
  };

  // Generate replace preview
  const generateReplacePreview = useCallback((searchText: string, replaceWith: string) => {
    if (!searchText || !replaceWith || !translationResults) {
      setReplacePreview([]);
      return;
    }

    const preview: {sid: string, oldText: string, newText: string, rowNumber: number}[] = [];
    
    translationResults.forEach((item, index) => {
      const turkishText = parseRunsText(item.runs) as string || '';
      
      let hasMatch = false;
      let regex: RegExp;
      
      if (isWholeWord) {
        if (isCaseSensitive) {
          // Case sensitive whole word
          const escapedSearchText = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const testRegex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchText})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
          hasMatch = testRegex.test(turkishText);
          // For replacement, capture groups and replace only the middle part
          regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchText})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
        } else {
          // Case insensitive whole word - normalize for Turkish chars
          const normalizedText = normalizeTurkish(turkishText);
          const normalizedSearchText = normalizeTurkish(searchText);
          const escapedSearchText = normalizedSearchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const testRegex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchText})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
          hasMatch = testRegex.test(normalizedText);
          
          // Create Turkish-aware case-insensitive pattern for whole word
          const searchPattern = Array.from(searchText).map(char => {
            const lower = char.toLocaleLowerCase('tr-TR');
            const upper = char.toLocaleUpperCase('tr-TR');
            if (lower !== upper) {
              const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              return `[${escapedLower}${escapedUpper}]`;
            }
            return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          }).join('');
          
          regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${searchPattern})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
        }
      } else {
        if (isCaseSensitive) {
          hasMatch = turkishText.includes(searchText);
          regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        } else {
          // Case insensitive - check with normalized text
          hasMatch = normalizeTurkish(turkishText).includes(normalizeTurkish(searchText));
          
          // Create a Turkish-aware case-insensitive regex by creating character classes
          const searchPattern = Array.from(searchText).map(char => {
            const lower = char.toLocaleLowerCase('tr-TR');
            const upper = char.toLocaleUpperCase('tr-TR');
            if (lower !== upper) {
              // Escape special regex chars
              const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              return `[${escapedLower}${escapedUpper}]`;
            }
            return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          }).join('');
          
          regex = new RegExp(searchPattern, 'g');
        }
      }
      
      if (hasMatch) {
        // For whole word with capture groups, preserve surrounding characters
        const newText = isWholeWord 
          ? turkishText.replace(regex, `$1${replaceWith}$3`)
          : turkishText.replace(regex, replaceWith);
        
        if (turkishText !== newText) {
          preview.push({
            sid: item.sid,
            oldText: turkishText,
            newText: newText,
            rowNumber: index + 1
          });
        }
      }
    });

    setReplacePreview(preview);
  }, [translationResults, parseRunsText, isCaseSensitive, isWholeWord]);

  // Single row replace function (translate_log_changes)
  const handleSingleReplace = async (sid: string) => {
    if (!currentDocumentId || !user?.id || !translationResults) {
      showError('Hata', 'Gerekli bilgiler eksik.');
      return;
    }

    try {
      const item = translationResults.find(d => d.sid === sid);
      if (!item || !item.runs) {
        showError('Hata', 'Satır bulunamadı.');
        return;
      }

      let regex: RegExp;
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      
      if (isWholeWord) {
        if (isCaseSensitive) {
          regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchTerm})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
        } else {
          // Case insensitive whole word - create Turkish-aware character classes
          const searchPattern = Array.from(searchTerm).map(char => {
            const lower = char.toLocaleLowerCase('tr-TR');
            const upper = char.toLocaleUpperCase('tr-TR');
            if (lower !== upper) {
              const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              return `[${escapedLower}${escapedUpper}]`;
            }
            return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          }).join('');
          
          regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${searchPattern})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
        }
      } else {
        if (isCaseSensitive) {
          regex = new RegExp(escapedSearchTerm, 'g');
        } else {
          // Case insensitive - create Turkish-aware character classes
          const searchPattern = Array.from(searchTerm).map(char => {
            const lower = char.toLocaleLowerCase('tr-TR');
            const upper = char.toLocaleUpperCase('tr-TR');
            if (lower !== upper) {
              const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              return `[${escapedLower}${escapedUpper}]`;
            }
            return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          }).join('');
          
          regex = new RegExp(searchPattern, 'g');
        }
      }
      
      const runs = JSON.parse(item.runs);
      
      if (Array.isArray(runs) && runs.length > 0) {
        const newRuns = runs.map((run: any) => {
          if (run.text && typeof run.text === 'string') {
            const updatedText = isWholeWord 
              ? run.text.replace(regex, `$1${replaceText}$3`)
              : run.text.replace(regex, replaceText);
            if (updatedText !== run.text) {
              return { ...run, text: updatedText };
            }
          }
          return run;
        });

        const newRunsJson = JSON.stringify(newRuns);

        console.log('🔄 Single Replace: Sending change to backend', { sid, documentId: currentDocumentId });

        const response = await fetch('https://n8n.mahir.ai/webhook/tr_log_changes', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          },
          body: JSON.stringify({
            document_id: currentDocumentId,
            user_id: user.id,
            sid: sid,
            runs: newRunsJson,
            timestamp: Math.floor(Date.now() / 1000)
          })
        });

        if (response.ok) {
          console.log('✅ Single Replace: Backend başarılı, refresh başlatılıyor');
          
          // Clear preview BEFORE refresh
          setReplacePreview(prev => prev.filter(p => p.sid !== sid));
          success('Başarılı', 'Değişiklik kaydedildi.');
          if (showToast) showToast('Değişiklik kaydedildi', 'success');
          
          // Refresh data after change
          if (onRefreshData) {
            console.log('🔄 Single Replace: onRefreshData çağrılıyor...');
            saveScrollPosition(); // Save scroll position before refresh
            await onRefreshData();
            console.log('✅ Single Replace: onRefreshData tamamlandı');
            
            // Also refresh change history
            console.log('🔄 Single Replace: fetchChangeHistory çağrılıyor...');
            await fetchChangeHistory();
            console.log('✅ Single Replace: fetchChangeHistory tamamlandı');
          }
        } else {
          throw new Error('Değişiklik kaydedilemedi');
        }
      }
    } catch (error) {
      console.error('Tek satır değiştirme hatası:', error);
      showError('Hata', 'Değişiklik kaydedilirken hata oluştu.');
    }
  };

  // Find and Replace function (multiple_translate_log_changes)
  const handleFindAndReplace = async () => {
    if (!searchTerm || !replaceText || !translationResults || !currentDocumentId || !user?.id) {
      showError('Hata', 'Lütfen arama metni ve değiştirilecek metni girin.');
      return;
    }

    setIsFindReplaceProcessing(true);

    try {
      const changes: { sid: string; runs: string }[] = [];
      let matchCount = 0;
      
      let regex: RegExp;
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      
      if (isWholeWord) {
        if (isCaseSensitive) {
          regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchTerm})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
        } else {
          // Case insensitive whole word - create Turkish-aware character classes
          const searchPattern = Array.from(searchTerm).map(char => {
            const lower = char.toLocaleLowerCase('tr-TR');
            const upper = char.toLocaleUpperCase('tr-TR');
            if (lower !== upper) {
              const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              return `[${escapedLower}${escapedUpper}]`;
            }
            return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          }).join('');
          
          regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${searchPattern})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
        }
      } else {
        if (isCaseSensitive) {
          regex = new RegExp(escapedSearchTerm, 'g');
        } else {
          // Case insensitive - create Turkish-aware character classes
          const searchPattern = Array.from(searchTerm).map(char => {
            const lower = char.toLocaleLowerCase('tr-TR');
            const upper = char.toLocaleUpperCase('tr-TR');
            if (lower !== upper) {
              const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              return `[${escapedLower}${escapedUpper}]`;
            }
            return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          }).join('');
          
          regex = new RegExp(searchPattern, 'g');
        }
      }

      translationResults.forEach(item => {
        const turkishText = parseRunsText(item.runs) as string || '';
        
        let hasMatch = false;
        if (isWholeWord) {
          if (isCaseSensitive) {
            hasMatch = regex.test(turkishText);
          } else {
            // For case-insensitive, check with normalized text for Turkish chars
            const normalizedText = normalizeTurkish(turkishText);
            const normalizedSearchTerm = normalizeTurkish(searchTerm);
            const normalizedRegex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${normalizedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
            hasMatch = normalizedRegex.test(normalizedText);
          }
        } else {
          if (isCaseSensitive) {
            hasMatch = turkishText.includes(searchTerm);
          } else {
            hasMatch = normalizeTurkish(turkishText).includes(normalizeTurkish(searchTerm));
          }
        }
        
        if (!hasMatch) return;
        
        if (item.runs && item.runs.trim() !== '' && item.runs !== '[]') {
          try {
            const runs = JSON.parse(item.runs);
            if (Array.isArray(runs) && runs.length > 0) {
              let hasChange = false;
              const newRuns = runs.map((run: any) => {
                if (run.text && typeof run.text === 'string') {
                  const matches = run.text.match(regex);
                  if (matches) matchCount += matches.length;
                  const newTextValue = isWholeWord 
                    ? run.text.replace(regex, `$1${replaceText}$3`)
                    : run.text.replace(regex, replaceText);
                  if (newTextValue !== run.text) {
                    hasChange = true;
                    return { ...run, text: newTextValue };
                  }
                }
                return run;
              });

              if (hasChange) {
                changes.push({ sid: item.sid, runs: JSON.stringify(newRuns) });
              }
            }
          } catch (e) {
            console.error('Runs parse hatası:', e);
          }
        }
      });

      if (changes.length === 0) {
        showError('Eşleşme Bulunamadı', `"${searchTerm}" için hiçbir eşleşme bulunamadı.`);
        setIsFindReplaceProcessing(false);
        return;
      }

      console.log('🔄 Find & Replace: Sending changes to backend', {
        changeCount: changes.length,
        matchCount,
        documentId: currentDocumentId
      });

      const response = await fetch('https://n8n.mahir.ai/webhook/multiple_tr_log_changes', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify({
          document_id: currentDocumentId,
          user_id: user.id,
          timestamp: Math.floor(Date.now() / 1000),
          changes: changes
        })
      });

      if (response.ok) {
        console.log('✅ Find & Replace: Backend başarılı, refresh başlatılıyor');
        success('Başarılı', `${matchCount} eşleşme "${searchTerm}" → "${replaceText}" ile değiştirildi.`);
        
        // Clear UI states BEFORE refresh
        setShowReplaceInput(false);
        setReplaceText('');
        setReplacePreview([]);
        
        if (showToast) showToast('Değişiklikler kaydedildi', 'success');
        
        // Refresh data after change
        if (onRefreshData) {
          console.log('🔄 Find & Replace: onRefreshData çağrılıyor...');
          saveScrollPosition(); // Save scroll position before refresh
          await onRefreshData();
          console.log('✅ Find & Replace: onRefreshData tamamlandı');
          
          // Also refresh change history
          console.log('🔄 Find & Replace: fetchChangeHistory çağrılıyor...');
          await fetchChangeHistory();
          console.log('✅ Find & Replace: fetchChangeHistory tamamlandı');
        }
      } else {
        showError('Hata', 'Değişiklikler kaydedilirken hata oluştu.');
      }
    } catch (error) {
      console.error('Find and replace hatası:', error);
      showError('Hata', 'Değişiklikler kaydedilirken hata oluştu.');
    } finally {
      setIsFindReplaceProcessing(false);
    }
  };

  // Fetch change history
  const fetchChangeHistory = useCallback(async () => {
    if (!currentDocumentId) return;
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/get_tr_log_change_history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: currentDocumentId })
      });
      if (response.ok) {
        const data = await response.json();
        setChangeHistory(data || []);
      }
    } catch (error) {
      console.error('Change history fetch error:', error);
    }
  }, [currentDocumentId]);

  // Fetch change history on mount and when document changes
  useEffect(() => {
    if (currentDocumentId) {
      fetchChangeHistory();
    }
  }, [fetchChangeHistory, currentDocumentId]);

  // Refresh change history when translation results change (after data refresh)
  useEffect(() => {
    if (currentDocumentId && translationResults && translationResults.length > 0) {
      fetchChangeHistory();
    }
  }, [translationResults, currentDocumentId]);

  // Handle search term change
  const handleSearchTermChange = (term: string) => {
    // If starting a new search (was empty, now has content)
    if (!searchTerm && term.trim()) {
      // Save current page and scroll position before search
      const container = getScrollContainer();
      if (container) {
        searchStateBeforeSearchRef.current = {
          page: currentPage,
          scrollPosition: container.scrollTop
        };
        console.log('🔖 Arama öncesi durum kaydedildi:', searchStateBeforeSearchRef.current);
      }
    }
    
    // If clearing search (was not empty, now empty)
    if (searchTerm && !term.trim()) {
      // Restore previous page and scroll position
      if (searchStateBeforeSearchRef.current) {
        const savedState = searchStateBeforeSearchRef.current;
        console.log('🔄 Arama öncesi duruma geri dönülüyor:', savedState);
        
        // First, update search term to empty
        onSearchTermChange(term);
        
        // Then restore page and scroll position after search term is cleared
        setTimeout(() => {
          console.log('📄 Sayfa geri yükleniyor:', savedState.page);
          onCurrentPageChange(savedState.page);
          
          // Restore scroll position after page change
          setTimeout(() => {
            const container = getScrollContainer();
            if (container) {
              container.scrollTo({ top: savedState.scrollPosition, behavior: 'smooth' });
              console.log('✅ Scroll pozisyonu geri yüklendi:', savedState.scrollPosition);
            }
          }, 150);
        }, 50);
        
        // Clear saved state
        searchStateBeforeSearchRef.current = null;
        
        // Clear search results
        setSearchResults([]);
        setCurrentSearchIndex(0);
        setHighlightedRowSid(null);
        
        // Return early to avoid calling onSearchTermChange again
        return;
      }
    }
    
    onSearchTermChange(term);
    
    if (term.trim()) {
      calculateSearchResults(translationResults, term);
    } else {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      setHighlightedRowSid(null);
    }
  };

  // Recalculate search results when search options change
  useEffect(() => {
    if (searchTerm.trim()) {
      calculateSearchResults(translationResults, searchTerm);
      if (showReplaceInput && replaceText) {
        generateReplacePreview(searchTerm, replaceText);
      }
    }
  }, [isCaseSensitive, isWholeWord]);

  // Helper function to create runs string from text
  const createRunsString = (text: string): string => {
    return JSON.stringify([{ text: text, bold: false }]);
  };

  // Drag and drop handlers for Turkish text
  const handleDragStart = (e: React.DragEvent, sid: string, turkishText: string) => {
    // Only allow drag if there's Turkish text content
    if (!turkishText || turkishText.trim() === '') {
      e.preventDefault();
      return;
    }
    
    setDraggedRowSid(sid);
    setIsDragging(true);
    
    // Set drag data
    e.dataTransfer.setData('text/plain', sid);
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedRowSid(null);
    setDragOverRowSid(null);
    setIsDragging(false);
    
    // Reset visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (e: React.DragEvent, sid: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Don't allow dropping on the same row
    if (draggedRowSid !== sid) {
      setDragOverRowSid(sid);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the row entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverRowSid(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetSid: string) => {
    e.preventDefault();
    
    const sourceSid = e.dataTransfer.getData('text/plain');
    
    if (!sourceSid || sourceSid === targetSid || !translationResults) {
      setDragOverRowSid(null);
      return;
    }

    // Find source and target rows
    const sourceRow = translationResults.find(item => item.sid === sourceSid);
    const targetRow = translationResults.find(item => item.sid === targetSid);
    
    if (!sourceRow || !targetRow) {
      setDragOverRowSid(null);
      return;
    }

    // Get the Turkish text from source row
    const sourceTurkishText = parseRunsText(sourceRow.runs) as string;
    
    if (!sourceTurkishText || sourceTurkishText.trim() === '') {
      setDragOverRowSid(null);
      return;
    }

    // Get the current Turkish text from target row
    const targetTurkishText = parseRunsText(targetRow.runs) as string;
    
    // Combine texts: target text + source text
    const combinedText = targetTurkishText && targetTurkishText.trim() !== '' 
      ? `${targetTurkishText} ${sourceTurkishText}` 
      : sourceTurkishText;

    try {
      if (onDragDropUpdate) {
        // Use the provided drag drop update function
        await onDragDropUpdate(sourceSid, targetSid, combinedText, '');
      } else {
        // Fallback to edit save functions
        const sourceIndex = translationResults.findIndex(item => item.sid === sourceSid);
        const targetIndex = translationResults.findIndex(item => item.sid === targetSid);
        
        if (sourceIndex !== -1 && targetIndex !== -1) {
          // Update target row with combined text
          onEditStart(targetIndex, combinedText);
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
          onEditSave(targetIndex);
          
          // Clear source row text
          onEditStart(sourceIndex, '');
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
          onEditSave(sourceIndex);
        }
      }

      // Show success message
      if (showToast) {
        showToast('Türkçe metin başarıyla taşındı', 'success');
      }
      
    } catch (error) {
      console.error('Drag and drop error:', error);
      if (showToast) {
        showToast('Metin taşıma sırasında hata oluştu', 'error');
      }
    }

    setDragOverRowSid(null);
  };

  return (
    <div 
      data-testid="translation-table-main-container"
      className="bg-green-50/70 dark:bg-green-900/30 backdrop-blur-sm border border-green-200 dark:border-green-800 rounded-2xl shadow-lg p-6 w-full flex flex-col"
    >
      {/* Sade Navigasyon */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={onBackToOptions}
            className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
            </svg>
            <span className="text-sm font-medium">{selectedCompany?.name}</span>
          </button>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Çeviri Yap
          </div>
        </div>
      </div>

      {/* Çeviri Butonu */}
      <div className="flex justify-start items-center gap-2 mb-4">
        <button
          onClick={onStartTranslation}
          disabled={!currentDocumentId || isUploading}
          className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 ${
            !currentDocumentId || isUploading
              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
          title="Çeviriyi başlat"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
          </svg>
          {isUploading ? 'Çevriliyor...' : 'Çeviriyi Başlat'}
        </button>
      </div>

      {/* Silme Butonları, Tablo İstatistikleri ve Arama */}
      <div className="flex items-center gap-3 mb-4">
        {/* En sol - Silme Butonları */}
        <div className="flex gap-3">
          {isSelectionMode ? (
            // Seçim modundayken gösterilen butonlar
            <>
              {selectedRows.size > 0 && (
                <div className="flex flex-col items-center gap-1 w-20">
                <button
                  onClick={onShowBulkDeleteModal}
                    className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-red-500 to-red-700"
                  title="Seçili satırların değişikliklerini sil"
                >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                    </div>
                    <div className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-yellow-400 rounded-full flex items-center justify-center border-2 border-white px-1">
                      <span className="text-xs font-bold text-gray-900">{selectedRows.size}</span>
                    </div>
                </button>
                  <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                    Seçili<br/>Değişiklikleri Sil
                  </span>
                </div>
              )}
              <div className="flex flex-col items-center gap-1 w-20">
              <button
                onClick={onExitSelectionMode}
                  className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-gray-500 to-gray-700"
                title="Seçim modundan çık"
              >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </div>
              </button>
                <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                  İptal
                </span>
              </div>
            </>
          ) : (
            // Normal modda gösterilen butonlar
            <>
              <div className="flex flex-col items-center gap-1 w-20">
              <button
                onClick={() => setShowChangedRowsOnly(!showChangedRowsOnly)}
                  className={`relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl ${
                    showChangedRowsOnly
                      ? 'bg-gradient-to-br from-purple-600 to-purple-800'
                      : 'bg-gradient-to-br from-purple-500 to-purple-700'
                  }`}
                title="Değişiklik olan satırları göster"
              >
                  <div className="absolute inset-0 flex items-center justify-center">
                    {showChangedRowsOnly ? (
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/>
                      </svg>
                    ) : (
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    )}
                  </div>
                  {showChangedRowsOnly && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
                      <span className="text-purple-600 text-[10px] font-bold">{filteredResults.length}</span>
                    </div>
                  )}
              </button>
                <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                  Değişiklik<br/>Olanlar
                </span>
              </div>
              <div className="flex flex-col items-center gap-1 w-20">
              <button
                onClick={onStartSelectionMode}
                  className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-blue-500 to-blue-700"
                title="Seçili değişiklikleri sil"
              >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                  </div>
              </button>
                <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                  Seçili<br/>Değişiklikleri Sil
                </span>
              </div>
              <div className="flex flex-col items-center gap-1 w-20">
              <button
                onClick={onShowDeleteChangesModal}
                  className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-red-500 to-red-700"
                title="Tüm değişiklikleri sil"
              >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                  </div>
              </button>
                <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                  Tüm<br/>Değişiklikleri Sil
                </span>
              </div>
            </>
          )}
        </div>

        {/* Ortada - Tablo İstatistikleri */}
        <div className="flex items-center gap-3">
          {/* Toplam Kayıt */}
          <div className="flex flex-col items-center gap-1 w-20">
            <div className="relative w-14 h-14 rounded-full shadow-lg bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
              <span className="text-white text-xs font-bold">{actualTotalItems}</span>
            </div>
            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
              Toplam<br/>Kayıt
            </span>
      </div>

        {/* Sayfa Başına Pie Chart */}
        <div 
          className="relative flex flex-col items-center gap-1 w-20"
          onMouseEnter={() => setShowPageSizePieMenu(true)}
          onMouseLeave={() => setShowPageSizePieMenu(false)}
        >
          <button
            className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-teal-500 to-teal-700"
            title="Sayfa başına kayıt sayısı"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white text-sm font-bold">{itemsPerPage}</span>
            </div>
          </button>
          <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
            Sayfa<br/>Başına
          </span>

          {/* Pie Chart Menu for Page Size */}
          {showPageSizePieMenu && (
            <div className="absolute z-50" style={{ left: '50%', top: '28px', marginLeft: '-90px', marginTop: '-90px' }}>
              <svg width="180" height="180" viewBox="0 0 180 180" className="pointer-events-auto drop-shadow-2xl">
                {/* 25 - Sol Üst (0° - 90°) */}
                <g 
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemsPerPageChange(25);
                    handlePageChange(1);
                  }}
                  className="cursor-pointer transition-all duration-300 hover:opacity-90"
                >
                  <path
                    d="M 90 90 L 90 18 A 72 72 0 0 1 162 90 Z"
                    fill={itemsPerPage === 25 ? "url(#gradient-teal-active)" : "url(#gradient-teal)"}
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text x="120" y="60" fontSize="16" fill="white" fontWeight="bold" textAnchor="middle">25</text>
                </g>

                {/* 50 - Sağ Alt (90° - 180°) */}
                <g 
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemsPerPageChange(50);
                    handlePageChange(1);
                  }}
                  className="cursor-pointer transition-all duration-300 hover:opacity-90"
                >
                  <path
                    d="M 90 90 L 162 90 A 72 72 0 0 1 90 162 Z"
                    fill={itemsPerPage === 50 ? "url(#gradient-teal-active)" : "url(#gradient-teal)"}
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text x="120" y="120" fontSize="16" fill="white" fontWeight="bold" textAnchor="middle">50</text>
                </g>

                {/* 100 - Sol Alt (180° - 270°) */}
                <g 
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemsPerPageChange(100);
                    handlePageChange(1);
                  }}
                  className="cursor-pointer transition-all duration-300 hover:opacity-90"
                >
                  <path
                    d="M 90 90 L 90 162 A 72 72 0 0 1 18 90 Z"
                    fill={itemsPerPage === 100 ? "url(#gradient-teal-active)" : "url(#gradient-teal)"}
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text x="60" y="120" fontSize="14" fill="white" fontWeight="bold" textAnchor="middle">100</text>
                </g>

                {/* 200 - Sol Üst (270° - 360°) */}
                <g 
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemsPerPageChange(200);
                    handlePageChange(1);
                  }}
                  className="cursor-pointer transition-all duration-300 hover:opacity-90"
                >
                  <path
                    d="M 90 90 L 18 90 A 72 72 0 0 1 90 18 Z"
                    fill={itemsPerPage === 200 ? "url(#gradient-teal-active)" : "url(#gradient-teal)"}
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text x="60" y="60" fontSize="14" fill="white" fontWeight="bold" textAnchor="middle">200</text>
                </g>

                <defs>
                  <linearGradient id="gradient-teal" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#14b8a6', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#0f766e', stopOpacity: 1 }} />
                  </linearGradient>
                  <linearGradient id="gradient-teal-active" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#2dd4bf', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#14b8a6', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
              </svg>
          </div>
          )}
        </div>

          {/* Satıra Git */}
          <div className="flex flex-col items-center gap-1">
            <input
              type="text"
              value={goToRowValue}
              onChange={handleGoToRowChange}
              placeholder="→"
              className="w-14 h-14 text-xs font-semibold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-lg"
              title="Satır numarasını yazın (otomatik olarak gider)"
            />
            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
              Satıra<br/>Git
            </span>
          </div>
        </div>

        {/* Kalan alan - Arama */}
        <div className="flex-1 flex items-end gap-2 mb-8">
          {/* Search Bar */}
          <div className="relative flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  handleSearchTermChange(value);
                  if (value.trim() && showReplaceInput && replaceText) {
                    generateReplacePreview(value, replaceText);
                  } else {
                    setReplacePreview([]);
                  }
                }}
                placeholder="Metinlerde ara..."
                className="block w-full pl-9 pr-28 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-lg"
              />
              {/* Search Options and Action Buttons */}
              {searchTerm && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {/* Case Sensitive Button */}
                  <button
                    onClick={() => setIsCaseSensitive(!isCaseSensitive)}
                    className={`p-1.5 rounded-md transition-all text-xs font-bold ${
                      isCaseSensitive 
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 ring-1 ring-blue-300 dark:ring-blue-600' 
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-400 dark:hover:bg-gray-500'
                    }`}
                    title="Büyük/Küçük Harf Duyarlı"
                  >
                    Aa
                  </button>
                  {/* Whole Word Button */}
                  <button
                    onClick={() => setIsWholeWord(!isWholeWord)}
                    className={`p-1.5 rounded-md transition-all ${
                      isWholeWord 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 ring-1 ring-green-300 dark:ring-green-600' 
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-400 dark:hover:bg-gray-500'
                    }`}
                    title="Tam Sözcük Eşleşmesi"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </button>
                  {/* Replace toggle button */}
                  <button
                    onClick={() => {
                      setShowReplaceInput(!showReplaceInput);
                      if (!showReplaceInput && replaceText) {
                        generateReplacePreview(searchTerm, replaceText);
                      } else {
                        setReplacePreview([]);
                      }
                    }}
                    className={`p-1.5 rounded-md transition-all ${
                      showReplaceInput 
                        ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 ring-1 ring-orange-300 dark:ring-orange-600' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                    }`}
                    title="Bul ve Değiştir"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                    </svg>
                  </button>
                  {/* Clear search button */}
                  <button
                    onClick={() => handleSearchTermChange('')}
                    className="p-1.5 rounded-md transition-all bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                    title="Aramayı Temizle"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
            
            {/* Replace input - Overlay */}
            {showReplaceInput && searchTerm && (
              <div className="absolute left-0 right-0 top-full mt-1 p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg shadow-xl space-y-3 z-50">
                <div className="flex items-start gap-2 w-full">
                  <input
                    type="text"
                    placeholder="Değiştirilecek metin..."
                    value={replaceText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setReplaceText(value);
                      if (value && searchTerm) {
                        generateReplacePreview(searchTerm, value);
                      } else {
                        setReplacePreview([]);
                      }
                    }}
                    className="flex-1 min-w-0 px-3 py-2 text-sm border border-orange-300 dark:border-orange-700 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleFindAndReplace}
                    disabled={!replaceText || isFindReplaceProcessing || replacePreview.length === 0}
                    className="px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"
                  >
                    {isFindReplaceProcessing ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="hidden sm:inline">İşleniyor</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                        </svg>
                        <span>Tümünü Değiştir</span>
                        <span className="bg-orange-500 px-1.5 py-0.5 rounded text-xs">{replacePreview.length}</span>
                      </>
                    )}
                  </button>
                </div>
                
                {/* Preview */}
                {replacePreview.length > 0 && (
                  <div className="max-h-96 overflow-y-auto space-y-2 bg-white dark:bg-gray-800 rounded-md p-3 border border-orange-200 dark:border-orange-800 w-full">
                    {replacePreview.map((item, idx) => {
                      const renderOldText = () => {
                        const parts: React.ReactElement[] = [];
                        let lastIndex = 0;
                        
                        let searchRegex: RegExp;
                        const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        
                        if (isWholeWord) {
                          if (isCaseSensitive) {
                            searchRegex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchTerm})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
                          } else {
                            // Case insensitive whole word - create Turkish-aware character classes
                            const searchPattern = Array.from(searchTerm).map(char => {
                              const lower = char.toLocaleLowerCase('tr-TR');
                              const upper = char.toLocaleUpperCase('tr-TR');
                              if (lower !== upper) {
                                const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                return `[${escapedLower}${escapedUpper}]`;
                              }
                              return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            }).join('');
                            
                            searchRegex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${searchPattern})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'g');
                          }
                        } else {
                          if (isCaseSensitive) {
                            searchRegex = new RegExp(escapedSearchTerm, 'g');
                          } else {
                            // Case insensitive - create Turkish-aware character classes
                            const searchPattern = Array.from(searchTerm).map(char => {
                              const lower = char.toLocaleLowerCase('tr-TR');
                              const upper = char.toLocaleUpperCase('tr-TR');
                              if (lower !== upper) {
                                const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                return `[${escapedLower}${escapedUpper}]`;
                              }
                              return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            }).join('');
                            
                            searchRegex = new RegExp(searchPattern, 'g');
                          }
                        }
                        
                        let match;
                        
                        while ((match = searchRegex.exec(item.oldText)) !== null) {
                          // For whole word mode, match[0] includes boundaries, match[2] is the actual word
                          const matchText = isWholeWord ? match[2] : match[0];
                          const matchStart = isWholeWord ? match.index + match[1].length : match.index;
                          
                          if (matchStart > lastIndex) {
                            parts.push(
                              <span key={`before-${lastIndex}`} className="text-gray-700 dark:text-gray-300">
                                {item.oldText.substring(lastIndex, matchStart)}
                              </span>
                            );
                          }
                          parts.push(
                            <span key={`match-${matchStart}`} className="text-red-600 dark:text-red-400 line-through bg-red-100 dark:bg-red-900/30 px-0.5 rounded">
                              {matchText}
                            </span>
                          );
                          lastIndex = matchStart + matchText.length;
                        }
                        if (lastIndex < item.oldText.length) {
                          parts.push(
                            <span key={`after-${lastIndex}`} className="text-gray-700 dark:text-gray-300">
                              {item.oldText.substring(lastIndex)}
                            </span>
                          );
                        }
                        return parts;
                      };
                      
                      const renderNewText = () => {
                        const parts: React.ReactElement[] = [];
                        let lastIndex = 0;
                        const replaceRegex = new RegExp(replaceText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                        let match;
                        
                        while ((match = replaceRegex.exec(item.newText)) !== null) {
                          if (match.index > lastIndex) {
                            parts.push(
                              <span key={`before-${lastIndex}`} className="text-gray-700 dark:text-gray-300">
                                {item.newText.substring(lastIndex, match.index)}
                              </span>
                            );
                          }
                          parts.push(
                            <span key={`replace-${match.index}`} className="text-green-600 dark:text-green-400 font-semibold bg-green-100 dark:bg-green-900/30 px-0.5 rounded">
                              {match[0]}
                            </span>
                          );
                          lastIndex = match.index + match[0].length;
                        }
                        if (lastIndex < item.newText.length) {
                          parts.push(
                            <span key={`after-${lastIndex}`} className="text-gray-700 dark:text-gray-300">
                              {item.newText.substring(lastIndex)}
                            </span>
                          );
                        }
                        return parts.length > 0 ? parts : <span className="text-gray-700 dark:text-gray-300">{item.newText}</span>;
                      };
                      
                      return (
                        <div key={idx} className="text-xs p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                          <div className="flex items-start gap-2 mb-1">
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="inline-flex items-center justify-center min-w-[2.5rem] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-mono text-[10px] font-semibold">
                                #{item.rowNumber}
                              </span>
                              <svg className="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-start gap-1">
                                <span className="text-red-500 dark:text-red-400 text-[10px] font-semibold mt-0.5">ESKİ:</span>
                                <div className="break-words flex-1">{renderOldText()}</div>
                              </div>
                              <div className="flex items-start gap-1">
                                <span className="text-green-600 dark:text-green-400 text-[10px] font-semibold mt-0.5">YENİ:</span>
                                <div className="break-words flex-1">{renderNewText()}</div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleSingleReplace(item.sid)}
                              className="flex-shrink-0 px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-semibold rounded transition-all flex items-center gap-1 self-center"
                              title="Bu satırı değiştir"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                              </svg>
                              Değiştir
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Search Results Navigation */}
          {searchTerm && searchResults.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-md">
              <span className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                {currentSearchIndex + 1}/{searchResults.length}
              </span>
              <button
                onClick={goToPreviousSearchResult}
                disabled={searchResults.length <= 1}
                className={`p-1 rounded transition-colors ${
                  searchResults.length <= 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200'
                }`}
                title="Önceki sonuç"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
              <button
                onClick={goToNextSearchResult}
                disabled={searchResults.length <= 1}
                className={`p-1 rounded transition-colors ${
                  searchResults.length <= 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200'
                }`}
                title="Sonraki sonuç"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Çeviri Sonuçları Tablosu */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden flex flex-col">
        {/* Sabit Tablo Başlığı */}
        <div className="bg-gray-50 dark:bg-gray-700" style={{ paddingRight: '8px' }}>
          <table className="min-w-full w-full divide-y divide-gray-200 dark:divide-gray-600 table-fixed">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              <th className="w-12 px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <span></span>
              </th>
              {isSelectionMode && (
                <th className="w-12 px-4 py-3">
                  {/* Checkbox column header */}
                </th>
              )}
                <th className="flex-1 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <span>Metin</span>
              </th>
            </tr>
          </thead>
        </table>
        </div>
        
        {/* Sadece tbody için scroll container */}
        <div 
          data-testid="translation-table-scroll-container"
          className="overflow-auto"
          style={{ height: '504px', minHeight: '504px' }}
        >
          <table className="min-w-full w-full table-fixed">
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
              {currentItems.map((result, index) => {
                const actualIndex = startIndex + index;
                const originalRowNumber = translationResults.findIndex(r => r.sid === result.sid) + 1;
                return (
                <tr 
                  key={`${result.sid}-${index}`} 
                  id={`row-${result.sid}`}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-500 ${
                    highlightedRowSid === result.sid 
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 shadow-lg animate-pulse' 
                      : ''
                  }`}
                >
                  <td className="w-12 px-2 py-3 text-sm text-gray-500 dark:text-gray-400 text-center font-mono border-r border-gray-200 dark:border-gray-600">
                    {originalRowNumber}
                  </td>
                  {isSelectionMode && (
                    <td className="w-12 px-4 py-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(result.sid)}
                          onChange={() => onRowSelect(result.sid)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                    </td>
                  )}
                  <td 
                    className={`flex-1 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 transition-all duration-200 border-r border-gray-200 dark:border-gray-600 ${
                      dragOverRowSid === result.sid 
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600' 
                        : ''
                    }`}
                    onDragOver={(e) => handleDragOver(e, result.sid)}
                    onDragLeave={(e) => handleDragLeave(e)}
                    onDrop={(e) => handleDrop(e, result.sid)}
                  >
                    {editingIndex === actualIndex ? (
                      // Edit mode
                      <div className="flex items-center gap-2">
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onInput={(e) => onEditingTextChange(e.currentTarget.textContent || '')}
                          className="flex-1 p-2 border-2 border-blue-500 dark:border-blue-400 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent break-words"
                          style={{ minHeight: '36px' }}
                        >
                          {parseRunsText(result.runs)}
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => onEditSave(actualIndex)}
                            className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                            title="Kaydet"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                            </svg>
                          </button>
                          <button
                            onClick={onEditCancel}
                            className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                            title="İptal"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View mode with drag and drop
                      <div className="flex items-start justify-between group">
                        <div 
                          className={`flex-1 pr-2 cursor-move transition-all duration-200 ${
                            parseRunsText(result.runs) && (parseRunsText(result.runs) as string).trim() !== ''
                              ? 'hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 -m-1'
                              : ''
                          } ${
                            draggedRowSid === result.sid 
                              ? 'opacity-50 bg-gray-200 dark:bg-gray-600 rounded p-1 -m-1' 
                              : ''
                          }`}
                          draggable={Boolean(parseRunsText(result.runs) && (parseRunsText(result.runs) as string).trim() !== '' && !isSelectionMode)}
                          onDragStart={(e) => handleDragStart(e, result.sid, parseRunsText(result.runs) as string)}
                          onDragEnd={handleDragEnd}
                          title={
                            parseRunsText(result.runs) && (parseRunsText(result.runs) as string).trim() !== '' && !isSelectionMode
                              ? 'Sürükleyip başka satıra taşıyabilirsiniz'
                              : undefined
                          }
                        >
                          {parseRunsText(result.runs)}
                          
                          {/* Change History Badge */}
                          {(() => {
                            const hasChangeHistory = changeHistory?.some((historyItem: any) => 
                              historyItem.sid === result.sid && historyItem.changes && historyItem.changes.length > 0
                            );
                            return hasChangeHistory && (
                              <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-md text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-300 dark:border-purple-700">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                Değişiklik Var
                              </span>
                            );
                          })()}
                          
                          {parseRunsText(result.runs) && (parseRunsText(result.runs) as string).trim() !== '' && !isSelectionMode && (
                            <div className="inline-flex items-center ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        {!isSelectionMode && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setSelectedRowChangeHistory({sid: result.sid, rowNumber: actualIndex + 1});
                                setShowChangeHistoryModal(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                              title="Değişiklik Geçmişi"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                            </button>
                            <button
                              onClick={() => onEditStart(actualIndex, parseRunsText(result.runs) as string)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                              title="Düzenle"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                              </svg>
                            </button>
                            <button
                              onClick={() => onShowDeleteRowModal(result.sid)}
                              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                              title="Bu satırın değişikliklerini sil"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sayfalama Kontrolleri */}
      {totalPages > 1 && (
        <div className="flex justify-center items-start mt-4">
          <div className="flex items-start gap-1">
            {/* İlk sayfa butonu */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                }`}
                title="İlk sayfa"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
                </svg>
              </button>
              <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 opacity-70">İlk</span>
            </div>
            
            {/* Önceki sayfa butonu */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                }`}
                title="Önceki sayfa"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
              <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 opacity-70">Önceki</span>
            </div>

            {/* Sayfa numaraları */}
            <div className="flex items-center gap-1 mx-2">
              {(() => {
                const pages = [];
                
                if (totalPages <= 7) {
                  // 7 veya daha az sayfa varsa hepsini göster
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                  }
                } else {
                  // 7'den fazla sayfa varsa akıllı gösterim
                  if (currentPage <= 4) {
                    // Başlangıçta: 1 2 3 4 5 ... son
                    for (let i = 1; i <= 5; i++) {
                      pages.push(i);
                    }
                    pages.push('...');
                    pages.push(totalPages);
                  } else if (currentPage >= totalPages - 3) {
                    // Sonda: 1 ... son-4 son-3 son-2 son-1 son
                    pages.push(1);
                    pages.push('...');
                    for (let i = totalPages - 4; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Ortada: 1 ... current-1 current current+1 ... son
                    pages.push(1);
                    pages.push('...');
                    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                      pages.push(i);
                    }
                    pages.push('...');
                    pages.push(totalPages);
                  }
                }
                
                return pages.map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-500 dark:text-gray-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page as number)}
                      className={`w-10 h-8 rounded text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ));
              })()}
            </div>

            {/* Sonraki sayfa butonu */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                }`}
                title="Sonraki sayfa"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
              <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 opacity-70">Sonraki</span>
            </div>
            
            {/* Son sayfa butonu */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                }`}
                title="Son sayfa"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
                </svg>
              </button>
              <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 opacity-70">Son</span>
            </div>
          </div>
        </div>
      )}

      {/* Change History Modal */}
      {showChangeHistoryModal && selectedRowChangeHistory && (
        <EnChangeHistoryModal
          isOpen={showChangeHistoryModal}
          onClose={() => {
            setShowChangeHistoryModal(false);
            setSelectedRowChangeHistory(null);
          }}
          rowSid={selectedRowChangeHistory.sid}
          changeHistory={changeHistory}
          isTurkish={true}
        />
      )}
    </div>
  );
};

export default TranslationTable;
